import {
  faChevronLeft,
  faCirclePlus,
  faLightbulb,
  faUsers,
} from "@fortawesome/sharp-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Table, TextInput, Loader } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "@/styles/components/settings.module.scss";
import { gql } from "@/__generated__";
import InlineBanner from "@/components/shared/InlineBanner";
import Navbar from "@/components/layout/Navbar";

export const GET_PROVIDER = gql(`
    query Provider {
        provider {
            users {
                email
            }
        }
    }
`);

export const ADD_PROVIDER_USER = gql(`
    mutation AddNewUserToProvider($email: String!) {
      addNewUserToProvider(email: $email)
    }
`);

type AddUserForm = {
  email: string;
};

function Settings() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<string[]>([]);

  const [addProviderUser, { loading, error, data: mutationData }] =
    useMutation(ADD_PROVIDER_USER);

  const { key, onSubmit, getInputProps, reset } = useForm<AddUserForm>({
    initialValues: {
      email: "",
    },
    validate: {
      email: (value) => {
        if (!value || value.trim() === "") {
          return "Email address is required";
        }
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) {
          return "Valid email address required";
        }
        return null;
      },
    },
  });

  const { loading: queryLoading, error: queryError } = useQuery(GET_PROVIDER, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (!data.provider) {
        throw new Error("Provider not found.");
      }

      setUsers(data.provider.users.map((user) => user.email));
    },
  });

  const addUser = async (formData: AddUserForm) => {
    const { email } = formData;
    await addProviderUser({
      variables: {
        email,
      },
      async onCompleted() {
        const newUsers = users;
        newUsers.push(email);
        setUsers(newUsers);

        reset();
      },
    });
  };

  return (
    <Navbar>
      <div className={styles.pageRoot}>
        <div className={styles.pageHeader}>
          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Go back"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          {"Settings"}
        </div>

        <div className={styles.usersSection}>
          <span className={styles.usersHeader}>
            <FontAwesomeIcon icon={faUsers} />
            {"Users"}
          </span>
          {mutationData && (
            <div className={styles.banner}>
              <InlineBanner
                type="success"
                message="Successfully added user"
                key="successBanner"
              />
            </div>
          )}
          {queryError && (
            <div className={styles.banner}>
              <InlineBanner
                type="error"
                message={queryError.message}
                key={queryError.message}
              />
            </div>
          )}
          {error && (
            <div className={styles.banner}>
              <InlineBanner
                type="error"
                message={error.message}
                key={error.message}
              />
            </div>
          )}
          <span className={styles.usersText}>
            {"Add coworkers who need to send referrals or track appeal status."}
          </span>
          <div className={styles.addUserForm}>
            <form
              id="addUser"
              className={styles.form}
              onSubmit={onSubmit(addUser)}
            >
              <TextInput
                label="Email address"
                key={key("email")}
                {...getInputProps("email")}
                classNames={{
                  input: styles.emailInput,
                  label: styles.emailLabel,
                }}
                onChange={(email) => {
                  getInputProps("email").onChange(email);
                }}
                data-testid="email_input"
              />
              <button
                className={`
                    ${styles.addUserButton}
                    ${loading ? "loading_button" : ""}
                    `}
                type="submit"
                disabled={loading}
                data-testid="add_user_button"
              >
                {loading ? (
                  <Loader color="black" />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCirclePlus} />
                    {"Add user"}
                  </>
                )}
              </button>
            </form>
          </div>
          {queryLoading && <Loader color="black" />}
          {!queryLoading && !queryError && (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{"Users"}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map((email) => (
                  <Table.Tr key={email}>
                    <Table.Td>{email}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          <div className={styles.usersDisclaimer}>
            <div className={styles.icon}>
              <FontAwesomeIcon icon={faLightbulb} title="Information" />
            </div>
            <div className={styles.copy}>
              <p className={styles.title}>{"Need to remove a user?"}</p>
              <p>
                {
                  "Email support and we'll be happy to take care of that for you."
                }
              </p>
              <a
                href="mailto:providers@getclaimable.com"
                className={styles.link}
              >
                {"Email - Providers@getclaimable.com"}
              </a>
            </div>
          </div>
        </div>
      </div>
    </Navbar>
  );
}

export default Settings;
